import { z } from "zod";

/**
 * Persona qualification (DEV-703) — the post-sign-in questionnaire.
 *
 * The question SET is code-owned: ids, types, option ids and `showIf` are the
 * constants below, and Solo edits only the displayed text and the order. The ids
 * are what analytics and every future segment are keyed on, so they must not be
 * editable in a CMS field; `showIf` is locked because the client resolves
 * visibility from the persona picks alone, and a condition it cannot evaluate
 * makes every submit fail validation.
 */

/** `^[a-z0-9_]+$` keeps ids usable as Mixpanel property names without escaping. */
const idRegex = /^[a-z0-9_]+$/;

export const ArenaQualificationQuestionIdSchema = z.string().trim().min(1).max(40).regex(idRegex);
export type ArenaQualificationQuestionId = z.infer<typeof ArenaQualificationQuestionIdSchema>;

export const ArenaQualificationOptionIdSchema = z.string().trim().min(1).max(40).regex(idRegex);

/**
 * `multi_choice` also carries a free-text option (`else`), which is OPTIONAL —
 * the option itself is the answer and the text only adds detail.
 */
export const ArenaQualificationQuestionKindSchema = z.enum(["single_choice", "multi_choice"]);
export type ArenaQualificationQuestionKind = z.infer<typeof ArenaQualificationQuestionKindSchema>;

export const ARENA_QUALIFICATION_FREE_TEXT_OPTION_ID = "else";
export const ARENA_QUALIFICATION_FREE_TEXT_MAX = 100;

/** Field caps, exported so the CMS inputs and these schemas cannot disagree. */
export const ARENA_QUALIFICATION_TEXT_MAX = 200;
export const ARENA_QUALIFICATION_HINT_MAX = 120;
export const ARENA_QUALIFICATION_OPTION_LABEL_MAX = 80;
/** No question offers more; an uncapped array reaches the mismatch message, which is logged whole. */
export const ARENA_QUALIFICATION_MAX_OPTIONS_PER_ANSWER = 20;

/** Shows this question only when `questionId` holds one of `optionIds`. */
export const ArenaQualificationShowIfSchema = z.object({
  questionId: ArenaQualificationQuestionIdSchema,
  optionIds: z.array(ArenaQualificationOptionIdSchema).min(1).max(ARENA_QUALIFICATION_MAX_OPTIONS_PER_ANSWER),
});
export type ArenaQualificationShowIf = z.infer<typeof ArenaQualificationShowIfSchema>;

export const ArenaQualificationOptionSchema = z.object({
  id: ArenaQualificationOptionIdSchema,
  label: z.string().trim().min(1).max(ARENA_QUALIFICATION_OPTION_LABEL_MAX),
});
export type ArenaQualificationOptionDto = z.infer<typeof ArenaQualificationOptionSchema>;

export const ArenaQualificationQuestionSchema = z.object({
  id: ArenaQualificationQuestionIdSchema,
  kind: ArenaQualificationQuestionKindSchema,
  text: z.string().trim().min(1).max(ARENA_QUALIFICATION_TEXT_MAX),
  hint: z.string().trim().max(ARENA_QUALIFICATION_HINT_MAX).nullish(),
  options: z.array(ArenaQualificationOptionSchema).min(2),
  showIf: ArenaQualificationShowIfSchema.nullish(),
  /**
   * May be submitted with no answer. Everything else must be covered exactly, so
   * without this flag a skipped question is indistinguishable from a stale
   * client and the whole submission is rejected.
   */
  optional: z.boolean().optional(),
});
export type ArenaQualificationQuestionDto = z.infer<typeof ArenaQualificationQuestionSchema>;

export const ARENA_QUALIFICATION_MAX_QUESTIONS = 10;

const QuestionsArraySchema = z
  .array(ArenaQualificationQuestionSchema)
  .min(1)
  .max(ARENA_QUALIFICATION_MAX_QUESTIONS)
  .superRefine((questions, ctx) => {
    if (new Set(questions.map((q) => q.id)).size !== questions.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "question ids must be unique" });
    }
    const byId = new Map(questions.map((q) => [q.id, q]));
    for (const question of questions) {
      if (new Set(question.options.map((o) => o.id)).size !== question.options.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate option id in ${question.id}` });
      }
      if (question.showIf == null) continue;
      // A `showIf` pointing at an id that does not exist would render a question
      // that can never appear — rejected at the boundary rather than shipped.
      const target = byId.get(question.showIf.questionId);
      if (target == null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${question.id} showIf targets unknown question` });
        continue;
      }
      const known = new Set(target.options.map((o) => o.id));
      if (question.showIf.optionIds.some((id) => !known.has(id))) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${question.id} showIf targets unknown option` });
      }
    }
  });

export const ArenaQualificationConfigSchema = z.object({ questions: QuestionsArraySchema });
export type ArenaQualificationConfigDto = z.infer<typeof ArenaQualificationConfigSchema>;

export const UpdateArenaQualificationConfigBodySchema = z.object({ questions: QuestionsArraySchema });
export type UpdateArenaQualificationConfigBody = z.infer<typeof UpdateArenaQualificationConfigBodySchema>;

/** A row exists only once the user answered or skipped — its existence IS the gate. */
export const ArenaQualificationOutcomeSchema = z.enum(["COMPLETED", "SKIPPED"]);
export type ArenaQualificationOutcome = z.infer<typeof ArenaQualificationOutcomeSchema>;

export const ArenaQualificationStateSchema = z.object({
  /** True once a response row exists. One-time per user, forever. */
  hasAnswered: z.boolean(),
  questions: QuestionsArraySchema,
});
export type ArenaQualificationStateDto = z.infer<typeof ArenaQualificationStateSchema>;

export const ArenaQualificationAnswerSchema = z.object({
  questionId: ArenaQualificationQuestionIdSchema,
  optionIds: z.array(ArenaQualificationOptionIdSchema).min(1).max(ARENA_QUALIFICATION_MAX_OPTIONS_PER_ANSWER),
  textValue: z.string().trim().max(ARENA_QUALIFICATION_FREE_TEXT_MAX).nullish(),
});
export type ArenaQualificationAnswerDto = z.infer<typeof ArenaQualificationAnswerSchema>;

export const SubmitArenaQualificationBodySchema = z.object({
  // Answers must cover exactly the questions VISIBLE given the answers
  // themselves — checked server-side against the active config, since the
  // visible set is not a compile-time constant.
  answers: z
    .array(ArenaQualificationAnswerSchema)
    .min(1)
    .max(ARENA_QUALIFICATION_MAX_QUESTIONS)
    .superRefine((answers, ctx) => {
      if (new Set(answers.map((a) => a.questionId)).size !== answers.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "each question may be answered only once" });
      }
    }),
});
export type SubmitArenaQualificationBody = z.infer<typeof SubmitArenaQualificationBodySchema>;
